import type { ReactNode } from "react";

import { Install } from "@similarweb/icons";
import type { ColumnType } from "@similarweb/pro-logic-components";
import { ChangeCell, ChipOverflowCell } from "@similarweb/pro-logic-components";
import type { StrictColor } from "@similarweb/styles";
import { colorsPalettes } from "@similarweb/styles";
import { Flex, Text } from "@similarweb/ui-components/foundations";

import { ShareBarCell } from "app/components/React/Table/cells";
import { NO_DATA_SIGN } from "app/filters/const";
import { translate } from "app/filters/i18nFilter";
import { percentageSignFilter } from "app/filters/ngFilters";
import { SourceBadge } from "app/pages/digital-marketing/generative-ai/llm-tracker/reports/components/source-badge/SourceBadge";
import type { ISourceType } from "app/pages/digital-marketing/generative-ai/llm-tracker/reports/components/source-badge/types";
import { AI_MODELS_CONFIG } from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/LLMConsts";
import { formatAveragePosition } from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/LLMTrackerUtils";

import type { IAiTrackerCampaign, LLModel } from "../../../shared/types";

import { TopicMenuCell } from "./TopicMenuCell";
import type { IItemTypeAndShare, ITopicVisibilityTableDataRaw } from "./types";

type TopicColumn = ColumnType<ITopicVisibilityTableDataRaw>;

const formatPercent = percentageSignFilter();

const renderPercentageWithChange = (
    value: number | undefined,
    change: number | null | undefined,
    automationId: string,
    valueColor?: StrictColor,
): ReactNode => {
    const formattedValue = formatPercent(value ?? 0, 2);
    const roundedChange = Math.abs(change ?? 0).toFixed(2);
    const isEffectivelyZero = parseFloat(roundedChange) === 0;

    return (
        <Flex
            alignItems="center"
            gap="4px"
            data-automation={automationId}
        >
            <Text
                as="span"
                fontVariant="dataTable2"
                color={valueColor}
            >
                {formattedValue}
            </Text>
            <Flex
                alignItems="center"
                justifyContent="center"
                style={{ width: 56 }}
            >
                <ChangeCell
                    value={isEffectivelyZero ? 0 : (change ?? 0)}
                    formattedValue={roundedChange}
                    noChangeSign="-"
                />
            </Flex>
        </Flex>
    );
};

const getTopicColumn = (campaign: IAiTrackerCampaign): TopicColumn => ({
    dataIndex: "topic",
    key: "topic",
    title: translate("overview_report.topics.visibility.widget.column.topic"),
    tooltipSubtitle: translate("overview_report.topics.visibility.widget.column.topic.tooltip"),
    align: "left",
    width: 250,
    fixed: "left",
    render: (value) => {
        const topicValue = (value ?? "") as string;
        const llmConfig = AI_MODELS_CONFIG[topicValue as Exclude<LLModel, LLModel.All>];
        if (llmConfig) {
            const { Icon } = llmConfig;
            return (
                <Flex
                    alignItems="center"
                    gap="8px"
                    data-automation={`topics-summary-llm-${topicValue}`}
                >
                    <Icon size={20} />
                    {translate(llmConfig.displayNameKey)}
                </Flex>
            );
        }
        return (
            <TopicMenuCell
                campaign={campaign}
                topic={topicValue}
            />
        );
    },
});

const getTopBrandsColumn = (coreCompetitors: Set<string>): TopicColumn => ({
    dataIndex: "topBrands",
    key: "topBrands",
    title: translate("overview_report.topics.visibility.widget.column.top-brands"),
    tooltipSubtitle: translate("overview_report.topics.visibility.widget.column.top-brands.tooltip"),
    align: "left",
    width: 250,
    render: (value) => {
        const topBrands = (value ?? []) as IItemTypeAndShare[];
        const labels = topBrands.map((brand) => ({
            text: brand.name ?? "",
            icon: coreCompetitors.has((brand.name ?? "").toLowerCase()) ? <Install /> : undefined,
        }));
        return <ChipOverflowCell labels={labels} />;
    },
});

const getFlatColumns = (): TopicColumn[] => [
    {
        dataIndex: "visibility",
        key: "visibility",
        title: translate("overview_report.topics.visibility.widget.column.visibility"),
        tooltipSubtitle: translate("overview_report.topics.visibility.widget.column.visibility.tooltip"),
        align: "left",
        width: 150,
        render: (_, record: ITopicVisibilityTableDataRaw) => (
            <ShareBarCell
                data-automation="visibility-cell"
                value={record.visibility}
                fractionDigits={record.visibility ? 2 : 0}
            />
        ),
    },
    {
        dataIndex: "brandMentionShareByTopics",
        key: "brandMentionShareByTopics",
        title: translate("overview_report.topics.visibility.widget.column.mention-share"),
        tooltipSubtitle: translate("overview_report.topics.visibility.widget.column.mention-share.tooltip"),
        align: "right",
        width: 75,
        render: (_, record: ITopicVisibilityTableDataRaw) => formatPercent(record.brandMentionShareByTopics ?? 0, 2),
    },
];

const getCompetitiveColumns = (): TopicColumn[] => [
    {
        dataIndex: "competitive" as keyof ITopicVisibilityTableDataRaw,
        key: "myRank",
        title: translate("overview_report.topics.visibility.widget.column.visibility.rank"),
        tooltipSubtitle: translate("overview_report.topics.visibility.widget.column.visibility.rank.tooltip"),
        align: "right",
        width: 100,
        render: (_, record: ITopicVisibilityTableDataRaw) => {
            const rank = record.competitive?.visibilityRank;
            return (
                <Text
                    as="span"
                    fontVariant="dataTable2"
                    color={rank ? colorsPalettes.carbon[400] : undefined}
                    data-automation="my-rank-cell"
                >
                    {rank ? `#${rank}` : NO_DATA_SIGN}
                </Text>
            );
        },
    },
    {
        dataIndex: "visibility",
        key: "myVisibility",
        title: translate("overview_report.topics.visibility.widget.column.visibility.my"),
        tooltipSubtitle: translate("overview_report.topics.visibility.widget.column.visibility.my.tooltip"),
        align: "left",
        width: 140,
        render: (_, record: ITopicVisibilityTableDataRaw) =>
            renderPercentageWithChange(record.visibility, record.visibilityChange, "my-visibility-cell"),
    },
    {
        dataIndex: "competitive" as keyof ITopicVisibilityTableDataRaw,
        key: "outOfCore",
        title: translate("overview_report.topics.visibility.widget.column.mention-share.out_of_core"),
        tooltipSubtitle: translate("overview_report.topics.visibility.widget.column.mention-share.out_of_core.tooltip"),
        align: "left",
        width: 140,
        render: (_, record: ITopicVisibilityTableDataRaw) =>
            renderPercentageWithChange(
                record.competitive?.outOfCompetitorsMentionShare ?? undefined,
                record.competitive?.outOfCompetitorsMentionShareChange,
                "out-of-core-cell",
                colorsPalettes.carbon[400],
            ),
    },
];

export const getColumnConfig = (campaign: IAiTrackerCampaign, hasCompetitiveSet: boolean): TopicColumn[] => {
    const coreCompetitors = new Set(
        (campaign?.competitors ?? []).flatMap(({ brands = [] }) => brands).map((b) => b.toLowerCase()),
    );
    return [
        getTopicColumn(campaign),
        ...(hasCompetitiveSet ? getCompetitiveColumns() : getFlatColumns()),
        {
            dataIndex: "averagePosition",
            key: "averagePosition",
            title: translate("overview_report.topics.visibility.widget.column.average_position"),
            tooltipSubtitle: translate("overview_report.topics.visibility.widget.column.average_position.tooltip"),
            width: 130,
            render: (_, record: ITopicVisibilityTableDataRaw) => formatAveragePosition(record.averagePosition),
        },
        getTopBrandsColumn(coreCompetitors),
        {
            dataIndex: "sourceTypes",
            key: "sourceTypes",
            title: translate("overview_report.topics.visibility.widget.column.source_types"),
            tooltipSubtitle: translate("overview_report.topics.visibility.widget.column.source_types.tooltip"),
            align: "left",
            width: 150,
            render: (value) => {
                const sourceTypes = (value ?? []) as IItemTypeAndShare[];
                return sourceTypes.length ? <SourceBadge sources={sourceTypes as ISourceType[]} /> : NO_DATA_SIGN;
            },
        },
    ];
};
